import Cookies from 'js-cookie';
import {DateTime} from 'luxon';

const setTokens = (data: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpireMills: number;
    refreshTokenExpireMills: number;
}) => {
    const {
        accessToken,
        accessTokenExpireMills,
        refreshToken,
        refreshTokenExpireMills,
    } = data;

    localStorage.setItem(
        'access-token',
        JSON.stringify({
            token: accessToken,
            expiredAt: DateTime.fromMillis(accessTokenExpireMills).toUTC(),
        }),
    );
    localStorage.setItem(
        'refresh-token',
        JSON.stringify({
            token: refreshToken,
            expiredAt: DateTime.fromMillis(refreshTokenExpireMills).toUTC(),
        }),
    );
};

const getAccessToken = (): string => {
    if (typeof window === 'undefined') return '';
    try {
        const value = localStorage.getItem('access-token');
        if (!value) return '';
        return JSON.parse(value).token ?? '';
    } catch {
        return '';
    }
};

const hasAccessTokenExpired = () => {
    const value = localStorage.getItem('access-token');

    if (!value) {
        return true;
    }

    const {expiredAt} = JSON.parse(value) as {expiredAt: string};

    return DateTime.fromISO(expiredAt).diffNow('seconds').seconds <= 0;
};

const getRefreshToken = () => {
    const value = localStorage.getItem('refresh-token');

    if (!value) {
        return '';
    }

    return JSON.parse(value).token as string;
};

const hasRefreshTokenExpired = () => {
    const value = localStorage.getItem('refresh-token');

    if (!value) {
        return true;
    }

    const {expiredAt} = JSON.parse(value) as {expiredAt: string};

    return DateTime.fromISO(expiredAt).diffNow('seconds').seconds <= 0;
};

const clearTokens = () => {
    ['access-token', 'refresh-token'].forEach((key) =>
        localStorage.removeItem(key),
    );
};

/**
 * @description cookie에 엑셀 다운로드 2차 인증 완료 (excel-download-verified) 플래그 저장
 * @param isVerified
 */
const setExcelDownloadVerifiedCookieFlag = (isVerified: 'true' | 'false') => {
    Cookies.set('excel-download-verified', isVerified, {
        expires: 1 / 24, // 1시간
        sameSite: 'Strict',
    });
};

/**
 * @description cookie에서 엑셀 다운로드 2차 인증 완료 (excel-download-verified) 플래그 조회
 */
const getExcelDownloadVerifiedCookieFlag = () => {
    const value = Cookies.get('excel-download-verified');

    if (!value) {
        return false;
    }

    return String(value).toLowerCase() === 'true';
};

/**
 * @description cookie에서 엑셀 다운로드 2차 인증 완료 (excel-download-verified) 플래그 초기화
 */
const clearExcelDownloadVerifiedCookieFlag = () => {
    Cookies.remove('excel-download-verified');
};

/**
 * @description 신기능 안내 툴팁 [x] 닫음 여부용 sessionStorage 키
 * - 같은 탭에서 새로고침 시 미노출, 로그아웃 후 로그인 시 노출
 */
export const NEW_FEATURE_TOOLTIP_DISMISSED_KEY =
    'businessTripNewFeatureTooltipDismissed';

/**
 * @description 로그아웃 시 신기능 툴팁 닫음 플래그 초기화 (다음 로그인 시 툴팁 노출)
 */
const clearNewFeatureTooltipDismissed = () => {
    sessionStorage.removeItem(NEW_FEATURE_TOOLTIP_DISMISSED_KEY);
};

export {
    setTokens,
    getAccessToken,
    getRefreshToken,
    hasAccessTokenExpired,
    hasRefreshTokenExpired,
    clearTokens,
    setExcelDownloadVerifiedCookieFlag,
    getExcelDownloadVerifiedCookieFlag,
    clearExcelDownloadVerifiedCookieFlag,
    clearNewFeatureTooltipDismissed,
};
